import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { submitAnswer } from '@/shared/api/review';
import type { Rating, ReviewQueueItem } from '@/shared/api/types';
import { formatInterval } from '@/shared/lib/interval';
import { currentTimeZone } from '@/shared/lib/timeZone';
import { Button, ErrorMessage } from '@/shared/ui';

import styles from './ReviewSession.module.css';

/**
 * Answer options. Worded around what happened in the learner's head rather
 * than around grades, and deliberately unlike Anki's labels and layout (PRD,
 * F11).
 */
export const ratingOptions: { rating: Rating; key: string }[] = [
  { rating: 'again', key: '1' },
  { rating: 'hard', key: '2' },
  { rating: 'good', key: '3' },
  { rating: 'easy', key: '4' },
];

export interface ReviewCardProps {
  item: ReviewQueueItem;
  onAnswered: () => void;
}

/** One card: the question, then the answer and the four ratings. */
export function ReviewCard({ item, onAnswered }: ReviewCardProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [revealed, setRevealed] = useState(false);
  const [clientReviewId] = useState(() => crypto.randomUUID());
  const shownAt = useRef(performance.now());

  const mutation = useMutation({
    mutationFn: ({ rating, durationMs }: { rating: Rating; durationMs: number }) =>
      submitAnswer({
        card_id: item.card.id,
        rating,
        duration_ms: durationMs,
        client_review_id: clientReviewId,
        time_zone: currentTimeZone(),
      }),
    onSuccess: onAnswered,
  });

  const answer = useCallback(
    (rating: Rating): void => {
      if (!mutation.isPending) {
        mutation.mutate({
          rating,
          durationMs: Math.max(0, Math.round(performance.now() - shownAt.current)),
        });
      }
    },
    [mutation],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.target instanceof HTMLElement && event.target.closest('input, textarea')) {
        return;
      }

      if (!revealed && (event.key === ' ' || event.key === 'Enter')) {
        event.preventDefault();
        setRevealed(true);

        return;
      }

      const option = ratingOptions.find((candidate) => candidate.key === event.key);
      if (revealed && option) {
        answer(option.rating);
      }
    };

    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [revealed, answer]);

  const { card } = item;
  const hasSource = card.source_excerpt !== '' || card.source_url !== '';

  return (
    <>
      <article className={styles.card} aria-label={t('review.card')}>
        <p className={styles.side}>{card.front}</p>
        {revealed && (
          <>
            <hr className={styles.divider} />
            <p className={styles.side}>{card.back}</p>
            {hasSource && (
              <details className={styles.source}>
                <summary>{t('decks.source')}</summary>
                {card.source_excerpt !== '' && (
                  <p className={styles.excerpt}>{card.source_excerpt}</p>
                )}
                {card.source_url !== '' && (
                  <a href={card.source_url} target="_blank" rel="noopener noreferrer">
                    {t('review.openSource')}
                  </a>
                )}
              </details>
            )}
          </>
        )}
      </article>

      <div className={styles.actions}>
        {revealed ? (
          <div className={styles.ratings}>
            {ratingOptions.map((option) => (
              <Button
                key={option.rating}
                className={[styles.rating, styles[option.rating]].join(' ')}
                disabled={mutation.isPending}
                onClick={() => {
                  answer(option.rating);
                }}
              >
                {t(`review.ratings.${option.rating}`)}
                <span className={styles.interval}>
                  {formatInterval(item.intervals[option.rating], t, i18n.language)}
                </span>
                <kbd className={styles.key}>{option.key}</kbd>
              </Button>
            ))}
          </div>
        ) : (
          <Button
            variant="primary"
            className={styles.show}
            onClick={() => {
              setRevealed(true);
            }}
          >
            {t('review.show')}
          </Button>
        )}
      </div>

      {mutation.isError && (
        <>
          <ErrorMessage error={mutation.error} />
          <Button
            onClick={() => {
              mutation.mutate(mutation.variables);
            }}
          >
            {t('common.retry')}
          </Button>
        </>
      )}
    </>
  );
}
